// src/pages/Users/Inscription/dossier/components/AdherentDossierPanel.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBell,
  FaCalendarAlt,
  FaCheck,
  FaCheckCircle,
  FaEuroSign,
  FaFileMedical,
  FaFilePdf,
  FaFileSignature,
  FaGoogleDrive,
  FaIdCard,
  FaImage,
  FaLink,
  FaLock,
  FaPlusCircle,
  FaSave,
  FaThumbsUp,
  FaTimesCircle,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getReglementInscriptionByIdAndSaison,
  updateReglementInscription,
} from "../../../../../services/reglementinscriptionServices";

import { sendNotificationToAdmin } from "../../../../../services/notificationServices";

import ValidateTrialModal from "../../ValidateTrialModal";
import EditDocumentsModal from "../../EditDocumentsModal";

import DossierActionButton from "../ui/DossierActionButton";

import {
  formatDate,
  toAbsoluteUrl,
  toBooleanFlag,
} from "../utils/dossierInscription.utils";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function isDrive(url) {
  return Boolean(
    url &&
      /(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
        url,
      ),
  );
}

function isImage(url) {
  return Boolean(
    url &&
      /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(
        String(url).split("?")[0],
      ),
  );
}

function isPdf(url) {
  const value = String(
    url || "",
  ).toLowerCase();

  return (
    value.endsWith(".pdf") ||
    value.includes(".pdf?")
  );
}

function getPaymentStatusClasses(
  status,
) {
  const map = {
    "Non Réglé":
      "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20",

    Réglé:
      "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20",

    "En cours":
      "bg-blue-50 text-[#013E8E] border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/20",

    "En Attente":
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20",

    Annulé:
      "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  };

  return (
    map[status] ||
    "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
  );
}

/* -------------------------------------------------------------------------- */
/*                               DocumentTile                                 */
/* -------------------------------------------------------------------------- */

function DocumentTile({
  url,
  title,
}) {
  const hasDocument =
    Boolean(url);

  let Icon = FaTimesCircle;

  let iconClasses =
    "bg-slate-100 text-slate-400 dark:bg-slate-800";

  let typeLabel =
    "Document absent";

  if (hasDocument) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;

      iconClasses =
        "bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300";

      typeLabel =
        "Google Drive";
    } else if (
      isImage(url)
    ) {
      Icon = FaImage;

      iconClasses =
        "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300";

      typeLabel = "Image";
    } else if (
      isPdf(url)
    ) {
      Icon = FaFilePdf;

      iconClasses =
        "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300";

      typeLabel = "PDF";
    } else {
      Icon = FaLink;

      iconClasses =
        "bg-blue-50 text-[#013E8E] dark:bg-blue-500/10 dark:text-blue-300";

      typeLabel = "Lien";
    }
  }

  return (
    <button
      type="button"
      disabled={!hasDocument}
      onClick={() => {
        if (!url) return;

        window.open(
          toAbsoluteUrl(url),
          "_blank",
          "noopener,noreferrer",
        );
      }}
      className={`
        flex
        w-full
        min-w-0
        items-center
        gap-3

        rounded-xl
        border

        px-3
        py-3

        text-left

        transition

        ${
          hasDocument
            ? `
              border-slate-200
              bg-white

              hover:border-blue-200
              hover:bg-blue-50/50

              dark:border-slate-700
              dark:bg-slate-900
            `
            : `
              cursor-not-allowed

              border-slate-200
              bg-slate-50

              dark:border-slate-800
              dark:bg-slate-900/50
            `
        }
      `}
    >
      <span
        className={`
          flex
          h-10 w-10
          shrink-0
          items-center
          justify-center

          rounded-xl

          ${iconClasses}
        `}
      >
        <Icon />
      </span>

      <span className="min-w-0 flex-1">
        <span
          className="
            block
            break-words

            text-xs
            font-bold

            text-slate-800

            dark:text-slate-200
          "
        >
          {title}
        </span>

        <span
          className="
            mt-0.5
            block

            text-[10px]
            text-slate-400
          "
        >
          {typeLabel}
          {hasDocument
            ? " · Ouvrir"
            : ""}
        </span>
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                              DossierSection                                */
/* -------------------------------------------------------------------------- */

function DossierSection({
  title,
  icon: Icon,

  validated,

  children,
}) {
  return (
    <section
      className={`
        overflow-hidden

        rounded-2xl

        border
        bg-white

        dark:bg-slate-900

        ${
          validated
            ? `
              border-emerald-200
              dark:border-emerald-500/20
            `
            : `
              border-slate-200
              dark:border-slate-800
            `
        }
      `}
    >
      <header
        className="
          flex
          items-center
          justify-between
          gap-3

          border-b
          border-slate-100

          px-4
          py-3

          dark:border-slate-800
        "
      >
        <div className="flex items-center gap-2">
          <span
            className="
              flex
              h-9 w-9
              items-center
              justify-center

              rounded-xl

              bg-slate-100

              text-slate-500

              dark:bg-slate-800
              dark:text-slate-300
            "
          >
            <Icon />
          </span>

          <h4
            className="
              text-sm
              font-black

              text-slate-900

              dark:text-white
            "
          >
            {title}
          </h4>
        </div>

        <span
          className={`
            inline-flex
            items-center
            gap-1

            rounded-full

            px-2
            py-1

            text-[9px]
            font-black
            uppercase

            ${
              validated
                ? `
                  bg-emerald-50
                  text-emerald-700

                  dark:bg-emerald-500/10
                  dark:text-emerald-300
                `
                : `
                  bg-slate-100
                  text-slate-500

                  dark:bg-slate-800
                  dark:text-slate-300
                `
            }
          `}
        >
          {validated ? (
            <>
              <FaCheck />
              Validé
            </>
          ) : (
            "En attente"
          )}
        </span>
      </header>

      <div className="p-4">
        {children}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                          AdherentDossierPanel                              */
/* -------------------------------------------------------------------------- */

export default function AdherentDossierPanel({
  adherent,
  dossier,

  user,

  settings,
  activeSeason,

  refreshDossier,
  refreshAll,
}) {
  const navigate =
    useNavigate();

  const [reglement, setReglement] =
    useState(null);

  const [
    isTrialOpen,
    setIsTrialOpen,
  ] = useState(false);

  const [
    isDocsOpen,
    setIsDocsOpen,
  ] = useState(false);

  const [
    editingType,
    setEditingType,
  ] = useState(false);

  const [
    typeReglement,
    setTypeReglement,
  ] = useState("");

  const [
    savingType,
    setSavingType,
  ] = useState(false);

  /* ---------------------------------------------------------------------- */
  /* Validations                                                            */
  /* ---------------------------------------------------------------------- */

  const cmValidated =
    toBooleanFlag(
      dossier?.validate_cm,
    );

  const licenceValidated =
    toBooleanFlag(
      dossier?.validate_licence,
    );

  const reglementValidated =
    toBooleanFlag(
      dossier?.validate_reglement,
    );

  const steps = [
    {
      label: "Médical",
      done: cmValidated,
      icon: FaFileMedical,
    },

    {
      label: "Licence",
      done: licenceValidated,
      icon: FaFileSignature,
    },

    {
      label: "Règlement",
      done: reglementValidated,
      icon: FaEuroSign,
    },
  ];

  const doneCount =
    steps.filter(
      (step) => step.done,
    ).length;

  const progress = Math.round(
    (doneCount /
      steps.length) *
      100,
  );

  /* ---------------------------------------------------------------------- */
  /* Documents                                                              */
  /* ---------------------------------------------------------------------- */

  const licenceSignerUrl =
    dossier?.formulaire_licence_a_signer ||
    "";

  const licenceSigneeUrl =
    dossier?.formulaire_licence_signee ||
    dossier?.formulaire_licence_signe ||
    "";

  const licenceUrl =
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    dossier?.licence_url ||
    "";

  /* ---------------------------------------------------------------------- */
  /* Règlement                                                              */
  /* ---------------------------------------------------------------------- */

  const loadReglement =
    async () => {
      if (
        !activeSeason ||
        !adherent?.id
      ) {
        return;
      }

      try {
        const data =
          await getReglementInscriptionByIdAndSaison(
            adherent.id,
            activeSeason,
          );

        setReglement(
          data || null,
        );
      } catch (error) {
        console.error(
          "Erreur règlement :",
          error,
        );

        setReglement(null);
      }
    };

  useEffect(() => {
    loadReglement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adherent?.id,
    activeSeason,
  ]);

  useEffect(() => {
    setTypeReglement(
      reglement?.type_reglement ||
        "",
    );
  }, [reglement]);

  const handleRefresh =
    async () => {
      await refreshDossier?.(
        adherent.id,
      );

      await loadReglement();
    };

  /* ---------------------------------------------------------------------- */
  /* Type de règlement                                                      */
  /* ---------------------------------------------------------------------- */

  const saveType =
    async () => {
      if (!reglement?.id) {
        return;
      }

      try {
        setSavingType(true);

        await updateReglementInscription(
          {
            id: reglement.id,

            type_reglement:
              typeReglement ||
              null,
          },
        );

        await loadReglement();

        setEditingType(false);

        toast.success(
          "Type de règlement mis à jour.",
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Impossible de modifier le type de règlement.",
        );
      } finally {
        setSavingType(false);
      }
    };

  /* ---------------------------------------------------------------------- */
  /* Total                                                                  */
  /* ---------------------------------------------------------------------- */

  const totalAmount =
    useMemo(() => {
      if (!reglement) {
        return 0;
      }

      return (
        Number(
          reglement.montant_cotisation ||
            0,
        ) +
        Number(
          reglement.montant_lic_assurance ||
            0,
        ) +
        Number(
          reglement.reduction_foyer ||
            0,
        ) +
        Number(
          reglement.pass_sport ||
            0,
        ) +
        Number(
          reglement.reduction_saison ||
            0,
        )
      );
    }, [reglement]);

  /* ---------------------------------------------------------------------- */
  /* Carte membre                                                           */
  /* ---------------------------------------------------------------------- */

  const handleMemberCard =
    () => {
      if (
        !reglementValidated
      ) {
        return;
      }

      navigate(
        "/dashboard/carte-membre",
        {
          state: {
            adherent,
            dossier,

            settings:
              settings || {},

            activeSeason:
              activeSeason ||
              adherent.saison_active ||
              "",
          },
        },
      );
    };

  return (
    <div className="space-y-4">
      {/* -------------------------------------------------------------- */}
      {/* Progression                                                     */}
      {/* -------------------------------------------------------------- */}

      <section
        className="
          rounded-2xl

          border
          border-slate-200

          bg-white

          p-4

          dark:border-slate-800
          dark:bg-slate-900
        "
      >
        <div className="flex items-end justify-between gap-3">
          <div>
            <p
              className="
                text-[9px]
                font-black
                uppercase
                tracking-[0.14em]
                text-slate-400
              "
            >
              Validation du bureau
            </p>

            <p
              className="
                mt-1
                font-black

                text-slate-900

                dark:text-white
              "
            >
              Avancement du dossier
            </p>
          </div>

          <strong
            className="
              text-2xl
              font-black

              text-[#013E8E]

              dark:text-blue-300
            "
          >
            {progress}%
          </strong>
        </div>

        <div
          className="
            mt-3
            h-2
            overflow-hidden
            rounded-full

            bg-slate-100

            dark:bg-slate-800
          "
        >
          <div
            className="
              h-full
              rounded-full

              bg-[#013E8E]

              transition-all

              dark:bg-blue-500
            "
            style={{
              width: `${progress}%`,
            }}
          />
        </div>

        <div
          className="
            mt-4
            grid
            grid-cols-3
            gap-2
          "
        >
          {steps.map(
            (step) => {
              const Icon =
                step.icon;

              return (
                <div
                  key={
                    step.label
                  }
                  className={`
                    rounded-xl
                    px-2
                    py-2.5

                    text-center

                    ${
                      step.done
                        ? `
                          bg-emerald-50
                          text-emerald-700

                          dark:bg-emerald-500/10
                          dark:text-emerald-300
                        `
                        : `
                          bg-slate-50
                          text-slate-400

                          dark:bg-slate-800/50
                        `
                    }
                  `}
                >
                  <Icon className="mx-auto" />

                  <p
                    className="
                      mt-1
                      text-[9px]
                      font-bold
                    "
                  >
                    {step.label}
                  </p>
                </div>
              );
            },
          )}
        </div>

        <DossierActionButton
          variant="primary"
          icon={FaPlusCircle}
          onClick={() =>
            setIsDocsOpen(true)
          }
          className="mt-4 w-full"
        >
          Ajouter ou modifier mes
          documents
        </DossierActionButton>
      </section>

      {/* -------------------------------------------------------------- */}
      {/* Sections                                                        */}
      {/* -------------------------------------------------------------- */}

      <div
        className="
          grid
          gap-3

          xl:grid-cols-3
        "
      >
        {/* Médical */}

        <DossierSection
          title="Médical"
          icon={FaFileMedical}
          validated={
            cmValidated
          }
        >
          <DocumentTile
            title="Certificat / Attestation"
            url={dossier.cm_url}
          />

          <div
            className="
              mt-3
              flex
              items-center
              gap-2

              text-xs

              text-slate-500

              dark:text-slate-400
            "
          >
            <FaCalendarAlt />

            {dossier.date_cm
              ? `Document du ${formatDate(
                  dossier.date_cm,
                )}`
              : "Date non renseignée"}
          </div>
        </DossierSection>

        {/* Licence */}

        <DossierSection
          title="Licence"
          icon={FaFileSignature}
          validated={
            licenceValidated
          }
        >
          <div className="space-y-2">
            <DocumentTile
              title="Formulaire signé"
              url={
                licenceSigneeUrl
              }
            />

            <DocumentTile
              title="Licence / Assurance"
              url={licenceUrl}
            />
          </div>

          {licenceSignerUrl ? (
            <button
              type="button"
              onClick={() =>
                window.open(
                  toAbsoluteUrl(
                    licenceSignerUrl,
                  ),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="
                mt-3

                text-xs
                font-bold

                text-[#013E8E]

                underline
                underline-offset-2

                dark:text-blue-300
              "
            >
              Télécharger le formulaire
              vierge
            </button>
          ) : null}
        </DossierSection>

        {/* Règlement */}

        <DossierSection
          title="Règlement"
          icon={FaEuroSign}
          validated={
            reglementValidated
          }
        >
          {reglement ? (
            <div className="space-y-3">
              <div>
                <p
                  className="
                    text-[9px]
                    font-black
                    uppercase
                    tracking-wide
                    text-slate-400
                  "
                >
                  Statut
                </p>

                <span
                  className={`
                    mt-1
                    inline-flex
                    rounded-full
                    border

                    px-2.5
                    py-1

                    text-[10px]
                    font-bold

                    ${getPaymentStatusClasses(
                      reglement.statut_reglement,
                    )}
                  `}
                >
                  {reglement.statut_reglement ||
                    "—"}
                </span>
              </div>

              {/* Type */}

              <div>
                <p
                  className="
                    text-[9px]
                    font-black
                    uppercase
                    tracking-wide
                    text-slate-400
                  "
                >
                  Type de règlement
                </p>

                {!editingType ? (
                  <div
                    className="
                      mt-1
                      flex
                      items-center
                      justify-between
                      gap-2
                    "
                  >
                    <span
                      className="
                        text-sm
                        font-bold

                        text-slate-700

                        dark:text-slate-200
                      "
                    >
                      {reglement.type_reglement ||
                        "Non renseigné"}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setEditingType(
                          true,
                        )
                      }
                      className="
                        text-xs
                        font-bold
                        text-[#013E8E]

                        dark:text-blue-300
                      "
                    >
                      Modifier
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <select
                      value={
                        typeReglement
                      }
                      onChange={(
                        event,
                      ) =>
                        setTypeReglement(
                          event.target
                            .value,
                        )
                      }
                      className="
                        w-full
                        rounded-xl

                        border
                        border-slate-200

                        bg-white

                        px-3
                        py-2

                        text-sm

                        dark:border-slate-700
                        dark:bg-slate-900
                      "
                    >
                      <option value="">
                        Sélectionner
                      </option>

                      <option value="En ligne">
                        En ligne
                      </option>

                      <option value="Chèque">
                        Chèque
                      </option>

                      <option value="Espèces">
                        Espèces
                      </option>

                      <option value="Virement">
                        Virement
                      </option>

                      <option value="CB">
                        CB
                      </option>

                      <option value="Autre">
                        Autre
                      </option>
                    </select>

                    <div className="flex gap-2">
                      <DossierActionButton
                        variant="success"
                        icon={FaSave}
                        onClick={
                          saveType
                        }
                        disabled={
                          savingType
                        }
                        className="flex-1"
                      >
                        Enregistrer
                      </DossierActionButton>

                      <DossierActionButton
                        onClick={() =>
                          setEditingType(
                            false,
                          )
                        }
                      >
                        Annuler
                      </DossierActionButton>
                    </div>
                  </div>
                )}
              </div>

              {/* Montants */}

              <div
                className="
                  rounded-xl

                  bg-slate-50

                  p-3

                  text-xs

                  dark:bg-slate-950/50
                "
              >
                <div
                  className="
                    flex
                    justify-between
                    gap-3
                  "
                >
                  <span className="text-slate-500">
                    Licence
                  </span>

                  <strong>
                    {Number(
                      reglement.montant_lic_assurance ||
                        0,
                    ).toFixed(2)}{" "}
                    €
                  </strong>
                </div>

                <div
                  className="
                    mt-1
                    flex
                    justify-between
                    gap-3
                  "
                >
                  <span className="text-slate-500">
                    Cotisation
                  </span>

                  <strong>
                    {Number(
                      reglement.montant_cotisation ||
                        0,
                    ).toFixed(2)}{" "}
                    €
                  </strong>
                </div>

                <div
                  className="
                    mt-3
                    flex
                    justify-between
                    gap-3

                    border-t
                    border-slate-200

                    pt-2

                    dark:border-slate-700
                  "
                >
                  <span className="font-bold">
                    Total
                  </span>

                  <strong
                    className="
                      text-base

                      text-[#013E8E]

                      dark:text-blue-300
                    "
                  >
                    {totalAmount.toFixed(
                      2,
                    )}{" "}
                    €
                  </strong>
                </div>
              </div>

              {!reglementValidated ? (
                <DossierActionButton
                  variant="primary"
                  onClick={() =>
                    navigate(
                      "/commandes-&-reglements",
                    )
                  }
                  className="w-full"
                >
                  Accéder aux règlements
                </DossierActionButton>
              ) : null}
            </div>
          ) : (
            <p
              className="
                text-sm
                leading-6
                text-slate-500

                dark:text-slate-400
              "
            >
              Aucun règlement enregistré
              pour cette saison.
            </p>
          )}
        </DossierSection>
      </div>

      {/* -------------------------------------------------------------- */}
      {/* Actions secondaires                                             */}
      {/* -------------------------------------------------------------- */}

      <section
        className="
          rounded-2xl

          border
          border-slate-200

          bg-white

          p-4

          dark:border-slate-800
          dark:bg-slate-900
        "
      >
        <p
          className="
            mb-3

            text-[9px]
            font-black
            uppercase
            tracking-[0.14em]
            text-slate-400
          "
        >
          Actions
        </p>

        <div
          className="
            grid
            grid-cols-1
            gap-2

            min-[420px]:grid-cols-2
          "
        >
          {adherent.statut_inscription ===
          "Cours d'essai" ? (
            <DossierActionButton
              variant="success"
              icon={FaThumbsUp}
              onClick={() =>
                setIsTrialOpen(true)
              }
              className="w-full"
            >
              Valider le cours d'essai
            </DossierActionButton>
          ) : null}

          {dossier.lien_drive ? (
            <>
              <DossierActionButton
                variant="primary"
                icon={FaGoogleDrive}
                onClick={() =>
                  window.open(
                    dossier.lien_drive,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="w-full"
              >
                Google Drive
              </DossierActionButton>

              <DossierActionButton
                icon={FaBell}
                onClick={async () => {
                  try {
                    await sendNotificationToAdmin(
                      `${user.prenom} ${user.nom} a déposé des documents pour ${adherent.prenom} ${adherent.nom} dans Google Drive.`,
                      user.token,
                    );

                    toast.success(
                      "Notification envoyée.",
                    );
                  } catch {
                    toast.error(
                      "Impossible d'envoyer la notification.",
                    );
                  }
                }}
                className="w-full"
              >
                Signaler le dépôt
              </DossierActionButton>
            </>
          ) : null}

          <DossierActionButton
            icon={
              reglementValidated
                ? FaIdCard
                : FaLock
            }
            variant={
              reglementValidated
                ? "primary"
                : "neutral"
            }
            disabled={
              !reglementValidated
            }
            onClick={
              handleMemberCard
            }
            className="w-full"
          >
            Carte membre
          </DossierActionButton>

          {reglementValidated ? (
            <DossierActionButton
              variant="success"
              icon={FaFilePdf}
              onClick={() => {
                navigate(
                  "/attestation",
                  {
                    state: {
                      prenom:
                        adherent.prenom,

                      nom:
                        adherent.nom,

                      dateNaissance:
                        formatDate(
                          adherent.date_naissance,
                        ),

                      adresse:
                        adherent.adresse,

                      cp:
                        adherent.cp,

                      commune:
                        adherent.commune,

                      cotisation:
                        reglement?.montant_cotisation,

                      licence:
                        reglement?.montant_lic_assurance,

                      reductionFoyer:
                        reglement?.reduction_foyer,

                      passSport:
                        reglement?.pass_sport,

                      reductionSaison:
                        reglement?.reduction_saison,

                      typeReglement:
                        reglement?.type_reglement,

                      nbReglement:
                        reglement?.nb_reglement,

                      statutReglement:
                        reglement?.statut_reglement,

                      observations:
                        reglement?.observations,

                      datePaiement:
                        new Date().toLocaleDateString(
                          "fr-FR",
                        ),

                      montant:
                        Number(
                          reglement?.montant_cotisation ||
                            0,
                        ) +
                        Number(
                          reglement?.montant_lic_assurance ||
                            0,
                        ),

                      president:
                        settings?.responsable_club,

                      association:
                        settings?.nom_club,

                      logo:
                        settings?.logo_club,

                      adresse_club:
                        settings?.adresse_club,

                      tel_club:
                        settings?.tel_club,

                      mail_club:
                        settings?.mail_club,

                      siteweb_club:
                        settings?.siteweb_club,

                      siret_club:
                        settings?.siret_club,

                      saison_active:
                        activeSeason,
                    },
                  },
                );
              }}
              className="w-full"
            >
              Attestation d'inscription
            </DossierActionButton>
          ) : null}
        </div>
      </section>

      {/* -------------------------------------------------------------- */}
      {/* Modales                                                         */}
      {/* -------------------------------------------------------------- */}

      <ValidateTrialModal
        isOpen={isTrialOpen}
        onClose={async (
          updated,
        ) => {
          setIsTrialOpen(false);

          if (updated) {
            await handleRefresh();

            await refreshAll?.();
          }
        }}
        adherent={adherent}
        user={user}
        currentGroupIds={
          Array.isArray(
            adherent.group_ids,
          )
            ? adherent.group_ids
            : adherent.group_id
              ? [
                  adherent.group_id,
                ]
              : undefined
        }
      />

      <EditDocumentsModal
        isOpen={isDocsOpen}
        onClose={() =>
          setIsDocsOpen(false)
        }
        dossier={dossier}
        refreshDossiers={
          handleRefresh
        }
      />
    </div>
  );
}